import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../Components/Layout';
import axios from 'axios';
import toast from 'react-hot-toast';
import { jwtDecode } from "jwt-decode";
import './SalarySlip.css';

const SalarySlip = () => {
    const navigate = useNavigate();
    const [allSalaryData, setAllSalaryData] = useState([]); // Store all data from API
    const [filteredData, setFilteredData] = useState([]); // Filtered data to display
    const [isLoading, setIsLoading] = useState(false);
    const [employeeId, setEmployeeId] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(''); // Empty - user selects
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear()); // Current year by default
    const [availableYears, setAvailableYears] = useState([]);
    const [isEmployeeIdLoaded, setIsEmployeeIdLoaded] = useState(false);
    const [availableMonths, setAvailableMonths] = useState([]);

    const getUserRole = (token) => {
        try {
            const decoded = jwtDecode(token);
            console.log("Decoded JWT:", decoded);

            const roles = decoded.roles || [];
            const userName = decoded.userName || decoded.sub || null;
            const employeeId = decoded.employeeId || null;
            const employeeFullName = decoded.employeeFullName || null;
            const expiryTime = decoded.exp ? decoded.exp * 1000 : null;

            console.log("Extracted employeeId:", employeeId);

            return { roles, userName, expiryTime, employeeId, employeeFullName };
        } catch (error) {
            console.error("Invalid token", error);
            return { roles: [], userName: null, expiryTime: null, employeeId: null, employeeFullName: null };
        }
    };

    // Get employee ID from token
    useEffect(() => {
        const token = localStorage.getItem('token');
        console.log('Token:', token);

        if (token) {
            try {
                const { employeeId } = getUserRole(token);
                console.log('Employee ID from token:', employeeId);

                if (employeeId) {
                    setEmployeeId(employeeId);
                    setIsEmployeeIdLoaded(true);
                    toast.success(`Employee ID loaded: ${employeeId}`);
                } else {
                    toast.error('Employee ID not found in token');
                    setIsEmployeeIdLoaded(true);
                }
            } catch (error) {
                console.error('Error decoding token:', error);
                toast.error('Invalid token. Please login again.');
                setIsEmployeeIdLoaded(true);
            }
        } else {
            toast.error('Please login again');
            setIsEmployeeIdLoaded(true);
        }
    }, []);

    // Generate available years (current and previous 2 years)
    useEffect(() => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let i = 0; i < 3; i++) {
            years.push(currentYear - i);
        }
        setAvailableYears(years);
    }, []);

    // Fetch all salary data for the employee
    const fetchAllSalaryData = useCallback(async () => {
        if (!employeeId) {
            console.log('No employee ID available');
            return;
        }

        console.log('Fetching all salary data for employee:', employeeId);

        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');

            if (!token) {
                toast.error('Authentication token not found');
                setIsLoading(false);
                return;
            }

            // Fetch all data without month/year params
            const url = `${process.env.REACT_APP_BACKEND_URL}/api/v1/salary/slip/employee/${employeeId}`;
            console.log('API URL:', url);

            const response = await axios.get(url, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            console.log('All Salary Data Response:', response.data);

            if (response.data && response.data.length > 0) {
                setAllSalaryData(response.data);

                // Get unique years and months from data
                const years = [...new Set(response.data.map(item => item.salaryYear))];
                const months = [...new Set(response.data.map(item => item.salaryMonth))];
                setAvailableYears(years.sort());
                setAvailableMonths(months.sort());

                // Filter data for current year by default
                const currentYear = new Date().getFullYear();
                const currentYearData = response.data.filter(item => item.salaryYear === currentYear);

                if (currentYearData.length > 0) {
                    // Transform and display current year data
                    const transformedData = currentYearData.map(employeeData => transformData(employeeData));
                    setFilteredData(transformedData);
                    toast.success(`Loaded ${transformedData.length} salary records for ${currentYear}`);
                } else {
                    // If no data for current year, show all data
                    const transformedData = response.data.map(employeeData => transformData(employeeData));
                    setFilteredData(transformedData);
                    toast.success(`Loaded ${transformedData.length} salary records`);
                }
            } else {
                setAllSalaryData([]);
                setFilteredData([]);
                toast.info('No salary data found for this employee');
            }
        } catch (error) {
            console.error('Error fetching salary data:', error);
            setAllSalaryData([]);
            setFilteredData([]);

            if (error.response) {
                console.log('Error response:', error.response);
                if (error.response.status === 404) {
                    toast.error('Salary data not found for this employee');
                } else if (error.response.status === 401) {
                    toast.error('Authentication failed. Please login again.');
                } else {
                    toast.error(error.response.data?.message || 'Failed to fetch salary data');
                }
            } else if (error.request) {
                console.log('No response received:', error.request);
                toast.error('No response from server. Please check your connection.');
            } else {
                toast.error('An unexpected error occurred.');
            }
        } finally {
            setIsLoading(false);
        }
    }, [employeeId]);

    // Transform API data to display format
    const transformData = (employeeData) => {
        const transformedData = {
            id: employeeData.empId || employeeData.employeeId,
            companyName: 'RAK SOFTECH PVT. LTD.',
            employeeNo: employeeData.empId,
            employeeName: employeeData.employeeName,
            designation: employeeData.designation,
            location: employeeData.location || 'Pune',
            dateOfJoining: employeeData.dateOfJoining,
            month: getMonthName(employeeData.salaryMonth),
            monthNumber: employeeData.salaryMonth,
            year: employeeData.salaryYear,
            attendance: {
                workingDays: employeeData.totalDaysInMonth,
                presentDays: employeeData.payableDays,
                leaveWithPay: employeeData.leaveWithPay,
                leaveWithoutPay: employeeData.leaveWithoutPay
            },
            earnings: [],
            deductions: [],
            totalEarnings: employeeData.totalEarning,
            totalDeductions: employeeData.totalDeduction,
            netSalary: employeeData.netSalary
        };

        if (employeeData.items && Array.isArray(employeeData.items)) {
            const earnings = employeeData.items.filter(item => item.componentType === "EARNING");
            const deductions = employeeData.items.filter(item => item.componentType === "DEDUCTION");

            transformedData.earnings = earnings.map(item => ({
                label: getEarningLabel(item.componentName),
                amount: Math.round(item.actualAmount)
            }));

            transformedData.deductions = deductions.map(item => ({
                label: getDeductionLabel(item.componentName),
                amount: Math.round(item.actualAmount)
            }));

            const maxLength = Math.max(transformedData.earnings.length, transformedData.deductions.length);
            while (transformedData.earnings.length < maxLength) {
                transformedData.earnings.push({ label: "", amount: 0 });
            }
            while (transformedData.deductions.length < maxLength) {
                transformedData.deductions.push({ label: "", amount: 0 });
            }
        }

        return transformedData;
    };

    // Fetch data when employee ID is loaded
    useEffect(() => {
        if (isEmployeeIdLoaded && employeeId) {
            console.log('Fetching all salary data...');
            fetchAllSalaryData();
        }
    }, [isEmployeeIdLoaded, employeeId, fetchAllSalaryData]);

    // Filter data when month or year changes
    useEffect(() => {
        if (allSalaryData.length === 0) return;

        let filtered = allSalaryData;

        // Filter by year
        filtered = filtered.filter(item => item.salaryYear === selectedYear);

        // Filter by month if selected
        if (selectedMonth) {
            filtered = filtered.filter(item => item.salaryMonth === parseInt(selectedMonth));
        }

        // Transform filtered data
        const transformedData = filtered.map(employeeData => transformData(employeeData));
        setFilteredData(transformedData);

        if (selectedMonth) {
            toast.success(`Showing data for ${getMonthName(parseInt(selectedMonth))} ${selectedYear}`);
        } else {
            toast.success(`Showing all data for ${selectedYear}`);
        }
    }, [allSalaryData, selectedMonth, selectedYear]);

    // Helper function to get month name
    const getMonthName = (monthNumber) => {
        const months = [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];
        return months[monthNumber - 1] || monthNumber;
    };

    // Helper function to get display label for earnings
    const getEarningLabel = (componentName) => {
        const labelMap = {
            'BASIC': 'Basic Pay',
            'HRA': 'HRA',
            'CONVEYANCE': 'Conveyance Allowance',
            'LTA': 'LTA',
            'MEDICAL': 'Medical Allowances',
            'SPECIAL_ALLOWANCE': 'Special Allowances',
            'PF_EMPLOYER': 'Other Reimbursements'
        };
        return labelMap[componentName] || componentName;
    };

    // Helper function to get display label for deductions
    const getDeductionLabel = (componentName) => {
        const labelMap = {
            'PROFESSIONAL_TAX': 'Prof. Tax',
            'PF_EMPLOYEE': 'P.F. Cont.',
            'ESIC': 'ESIC',
            'TDS': 'TDS'
        };
        return labelMap[componentName] || componentName;
    };

    const handleView = (month, year) => {
        // Find the specific data for the selected month/year to get monthNumber
        const specificData = allSalaryData.find(item =>
            item.salaryYear === year &&
            getMonthName(item.salaryMonth) === month
        );

        // Get the month number from the data, or use selectedMonth as fallback
        const monthNumber = specificData ? specificData.salaryMonth : selectedMonth;

        // Navigate with empId, month number, and year
        navigate('/salaryslip-pdf', {
            state: {
                empId: employeeId,
                month: monthNumber,
                year: year
            }
        });
    };

    const handleMonthChange = (e) => {
        const value = e.target.value;
        setSelectedMonth(value);
    };

    const handleYearChange = (e) => {
        const newYear = parseInt(e.target.value);
        setSelectedYear(newYear);
        // Clear month selection when year changes to avoid confusion
        setSelectedMonth('');
    };

    const handleManualFetch = () => {
        if (!employeeId) {
            toast.error('Employee ID not available');
            return;
        }
        fetchAllSalaryData();
    };

    // Get unique months available for the selected year
    const getAvailableMonthsForYear = () => {
        if (allSalaryData.length === 0) return [];
        const months = allSalaryData
            .filter(item => item.salaryYear === selectedYear)
            .map(item => item.salaryMonth);
        return [...new Set(months)].sort();
    };

    return (
        <Layout>
            <div className="background-main-content py-4">
                <div className="container">
                    <div className="salary-slip-wrapper">
                        <div className="salary-slip-card">
                            {/* Header */}
                            <div className="salary-slip-header">
                                <h2 className="company-name">RAK SOFTECH PVT. LTD.</h2>
                                <h5 className="slip-title">Salary Slip</h5>
                            </div>

                            {/* Filter Section */}
                            <div className="filter-section" style={{
                                padding: '15px 20px',
                                display: 'flex',
                                gap: '15px',
                                alignItems: 'center',
                                borderBottom: '1px solid #e0e0e0',
                                flexWrap: 'wrap'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <label style={{ fontWeight: '500', fontSize: '14px' }}>Month:</label>
                                    <select
                                        value={selectedMonth}
                                        onChange={handleMonthChange}
                                        style={{
                                            padding: '6px 12px',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px',
                                            fontSize: '14px',
                                            minWidth: '140px'
                                        }}
                                    >
                                        <option value="">All Months</option>
                                        {getAvailableMonthsForYear().map(month => (
                                            <option key={month} value={month}>
                                                {getMonthName(month)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <label style={{ fontWeight: '500', fontSize: '14px' }}>Year:</label>
                                    <select
                                        value={selectedYear}
                                        onChange={handleYearChange}
                                        style={{
                                            padding: '6px 12px',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px',
                                            fontSize: '14px'
                                        }}
                                    >
                                        {availableYears.map(year => (
                                            <option key={year} value={year}>
                                                {year}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <button
                                    onClick={handleManualFetch}
                                    disabled={isLoading || !employeeId}
                                    style={{
                                        padding: '6px 20px',
                                        backgroundColor: '#1a3a5c',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: (isLoading || !employeeId) ? 'not-allowed' : 'pointer',
                                        fontSize: '14px'
                                    }}
                                >
                                    {isLoading ? 'Loading...' : 'Refresh Data'}
                                </button>
                                {isLoading && <span style={{ marginLeft: '10px' }}>Loading...</span>}
                                {employeeId && (
                                    <span style={{ marginLeft: '10px', color: 'green', fontSize: '12px' }}>
                                        Employee: {employeeId}
                                    </span>
                                )}
                                {!employeeId && (
                                    <span style={{ marginLeft: '10px', color: 'red', fontSize: '12px' }}>
                                        Employee ID not available
                                    </span>
                                )}
                            </div>

                            {/* Employee Details Table */}
                            <div className="table-container">
                                {isLoading ? (
                                    <div style={{
                                        padding: '40px',
                                        textAlign: 'center',
                                        color: '#666'
                                    }}>
                                        <p>Loading salary data...</p>
                                    </div>
                                ) : filteredData.length > 0 ? (
                                    <table className="salary-table">
                                        <thead>
                                            <tr>
                                                <th>Employee No</th>
                                                <th>Employee Name</th>
                                                <th>Designation</th>
                                                <th>Month</th>
                                                <th>Year</th>
                                                <th className="action-column">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredData.map((data, index) => (
                                                <tr key={index}>
                                                    <td className="employee-no">{data.employeeNo}</td>
                                                    <td>{data.employeeName}</td>
                                                    <td>{data.designation}</td>
                                                    <td>{data.month}</td>
                                                    <td>{data.year}</td>
                                                    <td className="action-column">
                                                        <button
                                                            className="view-btn"
                                                            onClick={() => handleView(data.month, data.year)}
                                                        >
                                                            View
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <div style={{
                                        padding: '40px',
                                        textAlign: 'center',
                                        color: '#666'
                                    }}>
                                        <p>No salary data available</p>
                                        {employeeId && (
                                            <p style={{ fontSize: '12px', color: '#999', marginTop: '5px' }}>
                                                Employee ID: {employeeId}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Footer */}
                            <div className="salary-slip-footer">
                                <small>
                                    This is a computer generated payslip and does not require a signature
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default SalarySlip;